import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const VAPID_KEY =
  'BPbrOUMeNhtg_GWJ9SOHfqnPwymG_YHvcPYbj8tC3rY9cYML800o4tAV_D0qMIngw-SyjzLTpxircObfvRHwX94';

// Keep in sync with public/sw.js (service worker can't import TS modules)
const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyA19geU5kMBn7_DYx2MecYDAqeOsWIwlNE',
  authDomain: 'globemed-automation-488900.firebaseapp.com',
  projectId: 'globemed-automation-488900',
  storageBucket: 'globemed-automation-488900.firebasestorage.app',
  messagingSenderId: '57824888234',
  appId: '1:57824888234:web:29f7dade3ba34c663d17d3',
};

interface FirebaseMessaging {
  getToken(opts: unknown): Promise<string>;
  onMessage(cb: (payload: FcmPayload) => void): void;
}

interface FcmPayload {
  notification?: { title?: string; body?: string };
  data?: { title?: string; body?: string };
}

// Module-level singletons to avoid re-initializing Firebase
let _messaging: FirebaseMessaging | null = null;
let _initPromise: Promise<FirebaseMessaging | null> | null = null;
let _foregroundListenerSet = false;

declare const firebase: {
  initializeApp(config: Record<string, string>): void;
  messaging(): FirebaseMessaging;
};

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function initFirebase() {
  if (_messaging) return _messaging;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      await loadScript('https://www.gstatic.com/firebasejs/10.14.1/firebase-app-compat.js');
      await loadScript('https://www.gstatic.com/firebasejs/10.14.1/firebase-messaging-compat.js');
      firebase.initializeApp(FIREBASE_CONFIG);
      _messaging = firebase.messaging();
      return _messaging;
    } catch (e) {
      console.error('Firebase init failed:', e);
      _initPromise = null;
      return null;
    }
  })();

  return _initPromise;
}

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Wait for the SW to be fully active (handles race with main.tsx registration)
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export function usePushNotifications() {
  const registering = useRef(false);

  const registerToken = useCallback(async () => {
    // No-op if permission not granted or not supported
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (registering.current) return;
    registering.current = true;

    try {
      const messaging = await initFirebase();
      if (!messaging) return;

      const swReg = await ensureServiceWorker();
      if (!swReg) return;

      const token = await messaging.getToken({
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (token) {
        await api.push.register({ token });
      }
    } catch (e) {
      console.error('Push token registration failed:', e);
    } finally {
      registering.current = false;
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported' as const;

    const result = await Notification.requestPermission();
    if (result === 'granted') {
      await registerToken();
    }
    return result;
  }, [registerToken]);

  const setupForegroundListener = useCallback(async () => {
    if (_foregroundListenerSet) return;

    const messaging = await initFirebase();
    if (!messaging) return;

    _foregroundListenerSet = true;
    messaging.onMessage((payload) => {
      const data = payload.notification || payload.data || {};
      const title = data.title || 'RefDash';
      const body = data.body || '';
      toast(title, { description: body });
    });
  }, []);

  const unregisterToken = useCallback(async () => {
    try {
      const messaging = await initFirebase();
      if (!messaging) return;

      const swReg = await ensureServiceWorker();
      if (!swReg) return;

      const token = await messaging.getToken({
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (token) {
        await api.push.unregister({ token });
      }
    } catch (e) {
      console.error('Push token unregistration failed:', e);
    }
  }, []);

  return {
    registerToken,
    requestPermission,
    setupForegroundListener,
    unregisterToken,
  };
}
